/* ============================================================
   lh-how-to-search.js
   Page-scoped behaviour for /pages/how-to (body#how-to only),
   loaded deferred and gated to this page in layout/theme.liquid.

   The page is composed of separate sibling sections (4 rich-text
   headings + 4 page-faq accordion groups). This script groups each
   heading with the accordion group that follows it and nests them
   inside a native <details> outer accordion, MOVING the existing
   page-faq nodes in intact so Pipeline's inner accordion JS keeps
   working. No content, template, or section file is changed.

   Runs once on DOMContentLoaded. No MutationObserver.

   Commit 2: nested accordion + one-open-at-a-time.
   Commit 3: live keyword search (added below).
   ============================================================ */
(function () {
  'use strict';

  // Shared state across the accordion + search modules.
  var state = { searchActive: false };

  function buildAccordion() {
    if (document.body.id !== 'how-to') return null;
    var main = document.getElementById('MainContent');
    if (!main) return null;

    // Rich-text headings + page-faq groups, in document order.
    var nodes = Array.prototype.slice.call(
      main.querySelectorAll('[data-section-type="rich-text"], [data-section-type="page-faq"]')
    );
    if (!nodes.length) return null;

    // Pair each heading with the accordion group(s) that follow it.
    var groups = [];
    var current = null;
    nodes.forEach(function (sec) {
      var type = sec.getAttribute('data-section-type');
      var wrapper = sec.closest('.shopify-section') || sec;
      if (type === 'rich-text') {
        var headingEl = sec.querySelector('.standard__heading, h1, h2');
        current = {
          heading: headingEl ? headingEl.textContent.trim() : '',
          sourceWrappers: [wrapper],
          faqWrappers: []
        };
        groups.push(current);
      } else if (type === 'page-faq' && current) {
        current.faqWrappers.push(wrapper);
      }
    });

    // Defensive: drop any heading that has no accordion group under it.
    groups = groups.filter(function (g) { return g.faqWrappers.length; });
    if (!groups.length) return null;

    // Outer container shares the narrow column of the inner accordions.
    var outer = document.createElement('div');
    outer.className = 'how-to-outer wrapper--narrow';

    groups.forEach(function (g) {
      var item = document.createElement('details');
      item.className = 'how-to-outer__item';

      var summary = document.createElement('summary');
      summary.className = 'how-to-outer__trigger';

      var title = document.createElement('span');
      title.className = 'how-to-outer__title';
      title.textContent = g.heading;

      var icon = document.createElement('span');
      icon.className = 'how-to-outer__icon';
      icon.setAttribute('aria-hidden', 'true');

      summary.appendChild(title);
      summary.appendChild(icon);
      item.appendChild(summary);

      var panel = document.createElement('div');
      panel.className = 'how-to-outer__panel';
      g.faqWrappers.forEach(function (w) { panel.appendChild(w); }); // moved intact
      item.appendChild(panel);

      outer.appendChild(item);
      g.details = item;
      g.panel = panel;

      // Hide the now-redundant heading source section(s).
      g.sourceWrappers.forEach(function (w) { w.style.display = 'none'; });
    });

    // Insert directly after the page intro (H1 + intro paragraphs).
    var intro = main.querySelector('[data-section-type="page"]');
    var introWrap = intro ? (intro.closest('.shopify-section') || intro) : null;
    if (introWrap && introWrap.parentNode) {
      introWrap.parentNode.insertBefore(outer, introWrap.nextSibling);
    } else {
      main.insertBefore(outer, main.firstChild);
    }

    // One open at a time during normal browsing; suspended while searching.
    groups.forEach(function (g) {
      g.details.addEventListener('toggle', function () {
        if (!g.details.open || state.searchActive) return;
        groups.forEach(function (other) {
          if (other !== g && other.details.open) other.details.open = false;
        });
      });
    });

    return { groups: groups, outer: outer, state: state };
  }

  // ----------------------------------------------------------
  // Live keyword search: filters the accordions as you type.
  // ----------------------------------------------------------
  function buildSearch(ctx) {
    var groups = ctx.groups;

    var box = document.createElement('div');
    box.className = 'how-to-search wrapper--narrow';
    box.innerHTML =
      '<label for="how-to-search-input" class="visually-hidden">Search guides</label>' +
      '<div class="how-to-search__wrap">' +
        '<svg class="how-to-search__icon" aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">' +
          '<circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/>' +
          '<line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>' +
        '</svg>' +
        '<input id="how-to-search-input" type="search" autocomplete="off" ' +
          'placeholder="Search guides - e.g. cleaner, conditioner, dye" />' +
        '<button type="button" class="how-to-search__clear" aria-label="Clear search" hidden>×</button>' +
      '</div>' +
      '<p class="how-to-search__empty" role="status" hidden>No guides match that search. Try a different keyword.</p>';

    ctx.outer.parentNode.insertBefore(box, ctx.outer);

    var input = box.querySelector('#how-to-search-input');
    var clearBtn = box.querySelector('.how-to-search__clear');
    var emptyMsg = box.querySelector('.how-to-search__empty');

    // Index every inner accordion: heading + title + body text, lowercased.
    var items = [];
    groups.forEach(function (g) {
      var wraps = g.panel.querySelectorAll('.accordion__wrapper');
      Array.prototype.forEach.call(wraps, function (w) {
        var titleEl = w.querySelector('.accordion__title');
        var bodyEl = w.querySelector('.accordion__body');
        var text = (g.heading + ' ' +
          (titleEl ? titleEl.textContent : '') + ' ' +
          (bodyEl ? bodyEl.textContent : '')
        ).toLowerCase().replace(/\s+/g, ' ');
        items.push({ group: g, wrapper: w, title: titleEl, text: text });
      });
    });

    function reset() {
      ctx.state.searchActive = false;
      groups.forEach(function (g) {
        g.details.style.display = '';
        g.details.open = false;
      });
      items.forEach(function (it) {
        it.wrapper.style.display = '';
        it.wrapper.classList.remove('how-to--search-open');
        if (it.title) it.title.setAttribute('aria-expanded', 'false');
      });
      emptyMsg.hidden = true;
    }

    function runFilter() {
      var q = input.value.trim().toLowerCase();
      if (!q) { reset(); return; }

      // Suspend one-open-at-a-time so every matching section can show at once.
      ctx.state.searchActive = true;

      var total = 0;
      groups.forEach(function (g) { g._matches = 0; });

      items.forEach(function (it) {
        var hit = it.text.indexOf(q) !== -1;
        it.wrapper.style.display = hit ? '' : 'none';
        if (hit) {
          it.wrapper.classList.add('how-to--search-open');
          if (it.title) it.title.setAttribute('aria-expanded', 'true');
          it.group._matches++;
          total++;
        } else {
          it.wrapper.classList.remove('how-to--search-open');
          if (it.title) it.title.setAttribute('aria-expanded', 'false');
        }
      });

      groups.forEach(function (g) {
        var has = g._matches > 0;
        g.details.style.display = has ? '' : 'none';
        g.details.open = has;
      });

      emptyMsg.hidden = total !== 0;
    }

    var debounce;
    input.addEventListener('input', function () {
      clearBtn.hidden = input.value.length === 0;
      clearTimeout(debounce);
      debounce = setTimeout(runFilter, 120);
    });

    clearBtn.addEventListener('click', function () {
      input.value = '';
      clearBtn.hidden = true;
      runFilter();
      input.focus();
    });
  }

  // The visible H1 "HOW TO" lives in the page body content (Shopify Admin),
  // which is shared with the live theme, so we relabel it here on V3.2 only.
  // Guarded to the current text so a future Admin rename is left untouched.
  function renameHeading() {
    var main = document.getElementById('MainContent');
    if (!main) return;
    var h = main.querySelector('[data-section-type="page"] h1, [data-section-type="page"] h2');
    if (h && h.textContent.trim().toLowerCase() === 'how to') {
      h.textContent = 'How To Guides';
    }
  }

  function init() {
    if (document.body.id !== 'how-to') return;
    renameHeading();
    var ctx = buildAccordion();
    if (!ctx) return;
    window.__lhHowTo = ctx;
    buildSearch(ctx);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
